import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Sector
} from 'recharts';
import {
  TrendingUp, Filter, Download, RefreshCw, ChevronDown, ChevronRight,
  X, FileText, Building2, DollarSign, Package
} from 'lucide-react';
import apiClient from '../api/client';

// ── Colour palette ────────────────────────────────────────────────────────────
const COLOURS = [
  '#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6',
  '#8b5cf6','#ec4899','#14b8a6','#f97316','#84cc16',
  '#06b6d4','#a855f7','#e11d48','#0ea5e9','#22c55e'
];

const fmt = (n) =>
  n == null ? '—' : '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtShort = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return '£' + (n / 1_000_000).toFixed(1) + 'm';
  if (Math.abs(n) >= 1_000) return '£' + (n / 1_000).toFixed(1) + 'k';
  return '£' + Number(n).toFixed(0);
};

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, colour = 'indigo' }) {
  const colours = {
    indigo: 'from-indigo-500 to-indigo-600',
    amber:  'from-amber-500 to-amber-600',
    emerald:'from-emerald-500 to-emerald-600',
    blue:   'from-blue-500 to-blue-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colours[colour]} rounded-xl p-5 text-white shadow`}>
      <p className="text-xs font-medium uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1 truncate">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Custom bar tooltip ─────────────────────────────────────────────────────────
function BarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

// ── Active pie shape ──────────────────────────────────────────────────────────
function ActivePieShape(props) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <text x={cx} y={cy - 10} textAnchor="middle" fill="#1e293b" className="text-sm font-semibold" fontSize={13}>
        {payload.Category}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#64748b" fontSize={12}>
        {fmt(value)}
      </text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill="#94a3b8" fontSize={11}>
        {(percent * 100).toFixed(1)}%
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
}

// ── Drill-through panel ───────────────────────────────────────────────────────
function DrillPanel({ filters, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.set(k, v); });
    apiClient.get(`/spend-analytics/drill?${params}`)
      .then(r => setRows(r.data.invoices || []))
      .finally(() => setLoading(false));
  }, [filters]);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <FileText size={16} /> Invoice drill-through
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400">No invoices match the current filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr>
                  {['Date','Invoice #','Vendor','Department','GL Account','Category','Line £','Status'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.InvoiceDate ? new Date(r.InvoiceDate).toLocaleDateString('en-GB') : '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.InvoiceNumber || '—'}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[140px] truncate">{r.VendorName}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[120px] truncate">{r.DepartmentName}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[120px] truncate">{r.AccountCode ? `${r.AccountCode} ${r.AccountName}` : '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">{r.Category}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-slate-800">{fmt(r.LineAmount)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium
                        ${r.Status === 'Paid' ? 'bg-emerald-50 text-emerald-700' :
                          r.Status === 'Approved' ? 'bg-blue-50 text-blue-700' :
                          'bg-slate-100 text-slate-600'}`}>
                        {r.Status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="border-t px-5 py-3 text-xs text-slate-400">
          Showing up to 200 most recent matching invoice lines
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SpendAnalyticsPage() {
  const { currentCompanyId } = useAuth();

  // Filter state
  const [filters, setFilters] = useState({ financialYearId: 'all', category: 'all', departmentId: 'all', vendorId: 'all', dateFrom: '', dateTo: '' });
  const [filterOptions, setFilterOptions] = useState({ categories: [], departments: [], vendors: [], financialYears: [] });

  // Data state
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // UI state
  const [activePieIndex, setActivePieIndex] = useState(0);
  const [deptExpanded, setDeptExpanded] = useState(true);
  const [glExpanded, setGlExpanded] = useState(false);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillFilters, setDrillFilters] = useState({});

  // Load filter options once
  useEffect(() => {
    apiClient.get('/spend-analytics/filters')
      .then(r => {
        const data = r.data;
        setFilterOptions(data);
        // Default to most recent FY
        if (data.financialYears?.length) {
          setFilters(f => ({ ...f, financialYearId: data.financialYears[0].FinancialYearID }));
        }
      })
      .catch(() => {});
  }, [currentCompanyId]);

  // Load summary data when filters change
  const loadSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.set(k, v); });
    apiClient.get(`/spend-analytics/summary?${params}`)
      .then(r => setSummary(r.data))
      .catch(() => setError('Failed to load spend data.'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openDrill = (extraFilters = {}) => {
    setDrillFilters({ ...filters, ...extraFilters });
    setDrillOpen(true);
  };

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  const totals = summary?.totals || {};
  const monthly = summary?.monthly || [];
  const byDept = summary?.byDepartment || [];
  const byCategory = summary?.byCategory || [];
  const topVendors = summary?.topVendors || [];
  const topGL = summary?.topGLAccounts || [];

  // Build month-by-dept stacked bar data
  const allDepts = [...new Set(byDept.map(r => r.DepartmentName))];
  const monthKeys = [...new Map(monthly.map(r => [r.MonthLabel, r])).values()]
    .map(r => ({ label: r.MonthLabel, yr: r.yr, mo: r.mo }));
  const stackedData = monthKeys.map(mk => {
    const obj = { month: mk.label };
    allDepts.forEach(d => {
      const match = byDept.find(r => r.MonthLabel === mk.label && r.DepartmentName === d);
      obj[d] = match ? Number(match.TotalAmount) : 0;
    });
    return obj;
  });

  // Pie data
  const pieData = byCategory.map(r => ({ ...r, TotalAmount: Number(r.TotalAmount) }));

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-screen-2xl mx-auto">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <TrendingUp size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Spend Intelligence</h1>
            <p className="text-sm text-slate-500">Analyse spend by category, department, vendor and time period</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadSummary} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => openDrill()} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            <FileText size={14} /> View Invoices
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-700">
          <Filter size={14} /> Filters
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Financial Year */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Financial Year</label>
            <select value={filters.financialYearId} onChange={e => setFilter('financialYearId', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All years</option>
              {filterOptions.financialYears.map(fy => (
                <option key={fy.FinancialYearID} value={fy.FinancialYearID}>{fy.YearLabel || fy.FinancialYear}</option>
              ))}
            </select>
          </div>
          {/* Category */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Category</label>
            <select value={filters.category} onChange={e => setFilter('category', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All categories</option>
              {filterOptions.categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {/* Department */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Department</label>
            <select value={filters.departmentId} onChange={e => setFilter('departmentId', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All departments</option>
              {filterOptions.departments.map(d => (
                <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName}</option>
              ))}
            </select>
          </div>
          {/* Vendor */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Vendor</label>
            <select value={filters.vendorId} onChange={e => setFilter('vendorId', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All vendors</option>
              {filterOptions.vendors.map(v => (
                <option key={v.VendorID} value={v.VendorID}>{v.VendorName}</option>
              ))}
            </select>
          </div>
          {/* Date from */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">From date</label>
            <input type="date" value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
          {/* Date to */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">To date</label>
            <input type="date" value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
        </div>
        {/* Active filter chips */}
        {(filters.category !== 'all' || filters.departmentId !== 'all' || filters.vendorId !== 'all' || filters.dateFrom || filters.dateTo) && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
            {filters.category !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">
                {filters.category}
                <button onClick={() => setFilter('category', 'all')}><X size={10} /></button>
              </span>
            )}
            {filters.departmentId !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                {filterOptions.departments.find(d => d.DepartmentID === filters.departmentId)?.DepartmentName}
                <button onClick={() => setFilter('departmentId', 'all')}><X size={10} /></button>
              </span>
            )}
            {filters.dateFrom && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">
                From {filters.dateFrom}
                <button onClick={() => setFilter('dateFrom', '')}><X size={10} /></button>
              </span>
            )}
            {filters.dateTo && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">
                To {filters.dateTo}
                <button onClick={() => setFilter('dateTo', '')}><X size={10} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Spend" value={loading ? '…' : fmtShort(totals.TotalSpend)} sub={fmt(totals.TotalSpend)} colour="indigo" />
        <KpiCard label="Invoices" value={loading ? '…' : (totals.TotalInvoices ?? 0).toLocaleString()} colour="blue" />
        <KpiCard label="Vendors" value={loading ? '…' : (totals.TotalVendors ?? 0).toLocaleString()} colour="emerald" />
        <KpiCard label="Departments" value={loading ? '…' : (totals.TotalDepartments ?? 0).toLocaleString()} colour="amber" />
      </div>

      {/* ── Monthly Spend (stacked by department) ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Building2 size={15} className="text-indigo-500" /> Monthly Spend by Department
          </h2>
          {stackedData.length > 0 && (
            <button onClick={() => openDrill()} className="text-xs text-indigo-600 hover:underline">
              View invoices →
            </button>
          )}
        </div>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
        ) : stackedData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No data for selected filters</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stackedData} margin={{ top: 0, right: 10, left: 10, bottom: 30 }} barCategoryGap="30%" maxBarSize={40}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip content={<BarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {allDepts.map((dept, idx) => (
                <Bar key={dept} dataKey={dept} stackId="a" fill={COLOURS[idx % COLOURS.length]}
                  cursor="pointer" onClick={() => {
                    const deptObj = filterOptions.departments.find(d => d.DepartmentName === dept);
                    openDrill(deptObj ? { departmentId: deptObj.DepartmentID } : {});
                  }} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Category breakdown + Top Vendors ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Category pie */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Package size={15} className="text-amber-500" /> Spend by Category
          </h2>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
          ) : pieData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
              No GL categories assigned yet.{' '}
              <span className="text-indigo-600 ml-1">Tag GL accounts in Finance Settings → Chart of Accounts.</span>
            </div>
          ) : (
            <div className="flex gap-4">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} dataKey="TotalAmount" nameKey="Category"
                      cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                      activeIndex={activePieIndex}
                      activeShape={ActivePieShape}
                      onMouseEnter={(_, idx) => setActivePieIndex(idx)}
                      cursor="pointer"
                      onClick={(d) => openDrill({ category: d.Category })}>
                      {pieData.map((_, idx) => (
                        <Cell key={idx} fill={COLOURS[idx % COLOURS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legend list */}
              <div className="w-44 flex flex-col justify-center gap-1.5 py-2 overflow-auto max-h-56 text-xs">
                {pieData.map((d, idx) => {
                  const grand = pieData.reduce((s, r) => s + r.TotalAmount, 0);
                  const pct = grand > 0 ? (d.TotalAmount / grand * 100).toFixed(1) : 0;
                  return (
                    <button key={idx} onClick={() => { setActivePieIndex(idx); openDrill({ category: d.Category }); }}
                      className="flex items-center gap-1.5 text-left hover:bg-slate-50 rounded px-1 py-0.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLOURS[idx % COLOURS.length] }} />
                      <span className="truncate text-slate-700">{d.Category}</span>
                      <span className="ml-auto text-slate-500 flex-shrink-0">{pct}%</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Top vendors */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <DollarSign size={15} className="text-emerald-500" /> Top Vendors by Spend
          </h2>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
          ) : topVendors.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No vendor data</div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-auto">
              {topVendors.map((v, idx) => {
                const grand = topVendors[0]?.TotalAmount || 1;
                const pct = Math.round((v.TotalAmount / grand) * 100);
                return (
                  <button key={idx} onClick={() => openDrill({ vendorId: v.VendorID })}
                    className="w-full text-left group">
                    <div className="flex items-center justify-between text-xs text-slate-700 mb-0.5">
                      <span className="truncate mr-2 group-hover:text-indigo-600">{idx + 1}. {v.VendorName}</span>
                      <span className="flex-shrink-0 font-medium">{fmt(v.TotalAmount)}</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COLOURS[idx % COLOURS.length] }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{v.InvoiceCount} invoice{v.InvoiceCount !== 1 ? 's' : ''}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Department breakdown table ── */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <button onClick={() => setDeptExpanded(e => !e)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-slate-800 hover:bg-slate-50">
          <span className="flex items-center gap-2">
            <Building2 size={15} className="text-blue-500" /> Department Spend Summary
          </span>
          {deptExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        {deptExpanded && (
          <div className="overflow-x-auto border-t border-slate-100">
            {loading ? (
              <div className="h-24 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
            ) : byDept.length === 0 ? (
              <div className="h-24 flex items-center justify-center text-slate-400 text-sm">No data</div>
            ) : (() => {
                // Aggregate: dept → total
                const deptMap = {};
                byDept.forEach(r => {
                  if (!deptMap[r.DepartmentName]) deptMap[r.DepartmentName] = { name: r.DepartmentName, id: r.DepartmentID, total: 0, invoices: 0 };
                  deptMap[r.DepartmentName].total += Number(r.TotalAmount);
                  deptMap[r.DepartmentName].invoices += r.InvoiceCount;
                });
                const rows = Object.values(deptMap).sort((a, b) => b.total - a.total);
                const grandTotal = rows.reduce((s, r) => s + r.total, 0);
                return (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Department</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Total Spend</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Invoices</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">% of Spend</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-700 font-medium">{r.name}</td>
                          <td className="px-4 py-2 text-right text-slate-800 font-semibold">{fmt(r.total)}</td>
                          <td className="px-4 py-2 text-right text-slate-500">{r.invoices}</td>
                          <td className="px-4 py-2 text-right text-slate-500">
                            {grandTotal > 0 ? (r.total / grandTotal * 100).toFixed(1) + '%' : '—'}
                          </td>
                          <td className="px-4 py-2">
                            <button onClick={() => openDrill({ departmentId: r.id })}
                              className="text-xs text-indigo-600 hover:underline whitespace-nowrap">
                              View →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t">
                      <tr>
                        <td className="px-4 py-2 text-xs font-bold text-slate-700">Total</td>
                        <td className="px-4 py-2 text-right text-xs font-bold text-slate-800">{fmt(grandTotal)}</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                );
              })()
            }
          </div>
        )}
      </div>

      {/* ── Top GL Accounts ── */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <button onClick={() => setGlExpanded(e => !e)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-slate-800 hover:bg-slate-50">
          <span className="flex items-center gap-2">
            <FileText size={15} className="text-purple-500" /> Top GL Accounts by Spend
          </span>
          {glExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        {glExpanded && (
          <div className="overflow-x-auto border-t border-slate-100">
            {loading ? (
              <div className="h-24 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
            ) : topGL.length === 0 ? (
              <div className="h-24 flex items-center justify-center text-slate-400 text-sm">No GL account data</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Code</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Account</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Category</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Total Spend</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topGL.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">{r.AccountCode || '—'}</td>
                      <td className="px-4 py-2 text-slate-700">{r.AccountName || '—'}</td>
                      <td className="px-4 py-2">
                        <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">
                          {r.Category}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-800">{fmt(r.TotalAmount)}</td>
                      <td className="px-4 py-2 text-right text-slate-500">{r.InvoiceCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Drill panel ── */}
      {drillOpen && <DrillPanel filters={drillFilters} onClose={() => setDrillOpen(false)} />}
    </div>
  );
}
